/**
 *
 * CustomTable
 *
 */

import React, { memo, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
// import styled from 'styled-components';
import { Button, Card, Empty, Input, Select, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency } from "../../../utils/constants";
import useDeviceDetect from "../../../engine/hooks/useDeviceDetect";
import MobileCardsList from "./MobileCardsList";
import "./style.css";

const { Option } = Select;

function CustomTable({
  dataSource,
  columns,
  defaultPageSize,
  onChange = () => {},
  onSelectChange,
  hasSelection = false,
  hasPagination,
  rowKey,
  emptyText,
  pagination = {},
  expandable,
  smTitle = () => {},
  customClass,
  rowSelectedKeys,
  showHeader = true,
  duplicate,
  scroll,
  onRowLink = null,
  totalColumn,
  getCheckboxProps = () => {},
  customRowClassName = () => {},
  ...extraProps
}) {
  const navigate = useNavigate();
  const [selectedRowKeys, SetSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedInformationMenu, setSelectedInformationMenu] = useState("nb");

  const { isMobile } = useDeviceDetect(760);

  const useFocus = () => {
    const htmlElRef = useRef(null);
    const setFocus = () => {
      htmlElRef.current && htmlElRef.current.focus();
    };

    return [htmlElRef, setFocus];
  };

  useEffect(() => {
    if (JSON.stringify(rowSelectedKeys) !== JSON.stringify(selectedRowKeys)) {
      SetSelectedRowKeys(rowSelectedKeys);
    }
  }, [rowSelectedKeys]);

  const [inputRef, setInputFocus] = useFocus();

  const rowSelection = {
    selectedRowKeys,
    selections: true,
    onChange: r => {
      SetSelectedRowKeys(r);
      onSelectChange(r);
    },
    getCheckboxProps: getCheckboxProps,
  };

  const defaultOnRow = record => {
    return {
      customClass: "hello",
      className: customRowClassName(record),
    };
  };

  const onRow = (record, rowIndex) => {
    return {
      record,
      onClick: event => {
        // window.location.href = onRowLink(record); // This redirects the user when a row is clicked

        // record: row의 data
        // rowIndex: row의 index
        // event: event prototype
        // event.stopPropagation();
        if (
          event.target.className === "link-table" ||
          event.target.className === "ant-menu-title-content" ||
          event.target.className ===
            "ant-table-cell ant-table-selection-column ant-table-cell-row-hover"
        ) {
          return false;
        } else {
          if (event.metaKey || event.ctrlKey) {
            // Command/Ctrl+Click
            window.open(onRowLink(record), "_blank");
          } else {
            // Normal click
            navigate(onRowLink(record));
          }
          // event.pro
          // navigate(onRowLink(record));
        }
      },
      onMouseDown: event => {
        // Detect a right-click event
        if (event.button === 2) {
          const hiddenLink = event.currentTarget.querySelector(".hidden-link");
          if (hiddenLink) {
            hiddenLink.focus(); // Ensure the link receives focus
          }
        }
      },
    };
  };

  const getColumnSearchProps = (dataIndex, name, columnProps) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          autoFocus
          ref={inputRef}
          placeholder={`Rechercher dans ${name}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            // icon={<SearchOutlined />}
            size="small"
            style={{ width: 120 }}
          >
            Rechercher
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 60 }}
          >
            Réinitialiser
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => (
      <SearchOutlined
        style={{ color: filtered ? "#1890ff" : undefined }}
        onClick={setInputFocus}
      />
    ),
    onFilter: (value, record) => {
      const nameSplit = dataIndex.split(".");

      if (columnProps.searchIn !== undefined) {
        return columnProps
          .searchIn(record)
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      }

      // Sub 2 level
      if (nameSplit.length === 2) {
        if (record[nameSplit[0]] !== undefined) {
          if (record[nameSplit[0]][nameSplit[1]] !== undefined) {
            return record[nameSplit[0]][nameSplit[1]]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase());
          }
        }
      }

      // search in 3 level
      if (nameSplit.length === 3) {
        if (record[nameSplit[0]] !== undefined) {
          if (record[nameSplit[0]][nameSplit[1]] !== undefined) {
            if (
              record[nameSplit[0]][nameSplit[1]][nameSplit[2]] !== undefined
            ) {
              return record[nameSplit[0]][nameSplit[1]][nameSplit[2]]
                .toString()
                .toLowerCase()
                .includes(value.toLowerCase());
            }
          }
        }
      }

      return record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "";
    },
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(inputRef => inputRef.select(), 100);
      }
    },
    render: (text, record) =>
      searchedColumn === dataIndex
        ? columnProps.renderHighlight !== undefined
          ? columnProps.renderHighlight(
              text,
              record,
              <Highlighter
                highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                searchWords={[searchText]}
                autoEscape
                textToHighlight={text ? text.toString() : ""}
              />,
            )
          : columnProps.render(text, record)
        : columnProps.render !== undefined
        ? columnProps.render(text, record)
        : text,
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    setSearchText("");
    setSearchedColumn("");
    confirm();
  };

  let adaptedColumns = columns.map(col => {
    if (col.search) {
      return { ...col, ...getColumnSearchProps(col.dataIndex, col.title, col) };
    } else {
      return col;
    }
  });

  const renderSelectionInformation = () => {
    // Somme: Moyenne: Min : Max : Nombre :
    if (selectedRowKeys.length > 1 && totalColumn) {
      let filteredDataSource = dataSource.filter(item =>
        selectedRowKeys.includes(item._id),
      );
      if (filteredDataSource.length > 0) {
        let sum = filteredDataSource[0][totalColumn];
        let avg = 0;
        let min = filteredDataSource[0][totalColumn];
        let max = filteredDataSource[0][totalColumn];
        for (let i = 1; i < filteredDataSource.length; i++) {
          if (filteredDataSource[i][totalColumn] < min) {
            min = filteredDataSource[i][totalColumn];
          }
          if (filteredDataSource[i][totalColumn] > max) {
            max = filteredDataSource[i][totalColumn];
          }
          sum = sum + filteredDataSource[i][totalColumn];
        }
        avg = sum / filteredDataSource.length;
        let nb = selectedRowKeys.length;

        let displayText = "";
        switch (selectedInformationMenu) {
          case "nb":
            displayText = `Nombre : ${nb}`;
            break;
          case "sum":
            displayText = `Somme : ${formatCurrency(sum)}`;
            break;
          case "avg":
            displayText = `Moyenne : ${formatCurrency(avg)}`;
            break;
          case "max":
            displayText = `Max : ${formatCurrency(max)}`;
            break;
          case "min":
            displayText = `Min : ${formatCurrency(min)}`;
            break;
          default:
            displayText = `Nombre : ${nb}`;
        }

        return (
          <Select
            value={displayText}
            className={"selected-information-menu"}
            onChange={e => setSelectedInformationMenu(e)}
          >
            <Option value={"nb"}>Nombre : {nb}</Option>
            <Option value={"sum"}>Somme : {formatCurrency(sum)}</Option>
            <Option value={"avg"}>Moyenne : {formatCurrency(avg)}</Option>
            <Option value={"min"}>Min : {formatCurrency(min)}</Option>
            <Option value={"max"}>Max : {formatCurrency(max)}</Option>
          </Select>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  return isMobile ? (
    <MobileCardsList
      dataSource={dataSource}
      columns={columns.filter(
        e => e.visible === true || e.visible === undefined,
      )}
      onRowLink={onRowLink}
      rowKey={rowKey}
      smTitle={smTitle}
    />
  ) : (
    <Card
      bordered={false}
      style={{ backgroundColor: "#fff", padding: 0 }}
      bodyStyle={{ padding: 5 }}
    >
      <Table
        showHeader={showHeader}
        onRow={onRowLink === null ? defaultOnRow : onRow}
        rowKey={rowKey}
        scroll={scroll ? scroll : { y: "100%" }}
        style={{ backgroundColor: "#fff" }}
        className={
          "custom-table" +
          " " +
          customClass +
          (onRowLink === null ? "" : " rowHover")
        }
        dataSource={dataSource}
        columns={adaptedColumns.filter(
          e => e.visible === true || e.visible === undefined,
        )}
        size={"small"}
        onChange={(pagination, filters, sorter, extra) =>
          onChange(pagination, filters, sorter, extra)
        }
        rowSelection={hasSelection !== false ? rowSelection : null}
        expandable={expandable}
        {...extraProps}
        pagination={
          hasPagination
            ? {
                hideOnSinglePage: true,
                position: ["topRight"],
                defaultPageSize: 50,
                pageSizeOptions: [10, 20, 50, 100],
                showSizeChanger: true,
              }
            : pagination
        }
        locale={{
          emptyText: (
            <Empty description={emptyText ? emptyText : "Aucune donnée"} />
          ),
        }}
      />
      {selectedRowKeys ? renderSelectionInformation() : null}
    </Card>
  );
}

CustomTable.propTypes = {
  hasSelection: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

CustomTable.defaultProps = {
  onSelectChange: () => {},
  rowKey: record => record._id,
  hasPagination: false,
};

export default memo(CustomTable);
